import { useEffect, useState } from "react";
import { ShieldCheck, ShieldAlert, History } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const EVENT_IT = { "sms_2ck.sender_confirmed": "Conferma manuale", "sms_2ck.sender_revoked": "Revoca", "sms_2ck.sender_confirmation_invalidated": "Invalidazione automatica" };
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("it-IT") : "—");

function ConfirmForm({ senderId, senderValue, onDone }) {
  const [f, setF] = useState({ checked: false, source: "", verified_on: "", note: "" });
  const [busy, setBusy] = useState(false);
  const valid = f.checked && f.source && f.verified_on && f.note.trim().length >= 10;
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/sms/2ck/senders/${senderId}/confirm`, { confirm: true, source: f.source, verified_on: f.verified_on, note: f.note });
      toast.success(data.changed ? "Mittente confermato manualmente (audit registrato)" : "Mittente già confermato: nessuna modifica");
      onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-2 pt-2 border-t border-amber-500/30" data-testid="sms-2ck-confirm-form">
      <p className="text-[11px] text-amber-600">Procedura di verifica documentata (solo Super Admin). La conferma vale esclusivamente per il mittente <b>{senderValue || "—"} (id {senderId})</b>.</p>
      <label className="flex items-start gap-2 text-xs cursor-pointer">
        <input type="checkbox" className="mt-0.5" checked={f.checked} onChange={(e) => setF({ ...f, checked: e.target.checked })} data-testid="sms-2ck-confirm-checkbox" />
        <span>Confermo di aver verificato personalmente che questo mittente è attivo e abilitato all'invio presso 2CK.</span>
      </label>
      <div className="grid sm:grid-cols-2 gap-2">
        <div className="space-y-1">
          <Label className="text-[11px]">Fonte della verifica</Label>
          <select className="w-full h-9 rounded-md border border-input bg-background text-foreground px-2 text-xs" value={f.source} onChange={(e) => setF({ ...f, source: e.target.value })} data-testid="sms-2ck-confirm-source">
            <option value="">— scegli —</option>
            <option value="panel">Pannello ufficiale 2CK</option>
            <option value="provider">Conferma scritta del provider</option>
          </select>
        </div>
        <div className="space-y-1">
          <Label className="text-[11px]">Data della verifica</Label>
          <Input type="date" className="text-xs" value={f.verified_on} max={new Date().toISOString().slice(0, 10)} onChange={(e) => setF({ ...f, verified_on: e.target.value })} data-testid="sms-2ck-confirm-date" />
        </div>
      </div>
      <div className="space-y-1">
        <Label className="text-[11px]">Nota obbligatoria (min 10 caratteri: cosa hai verificato, con chi, riferimenti)</Label>
        <Textarea className="text-xs min-h-[60px]" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} data-testid="sms-2ck-confirm-note" />
      </div>
      <Button size="sm" onClick={submit} disabled={!valid || busy} data-testid="sms-2ck-confirm-submit"><ShieldCheck className="h-4 w-4 mr-1" /> Conferma mittente</Button>
    </div>
  );
}

function RevokeForm({ senderId, onDone }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [checked, setChecked] = useState(false);
  const submit = async () => {
    try {
      const { data } = await api.post(`/sms/2ck/senders/${senderId}/revoke`, { confirm: true, reason });
      toast.success(data.changed ? "Conferma revocata: mittente non più utilizzabile (audit registrato)" : "Nessuna conferma attiva da revocare");
      setOpen(false); onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e)); }
  };
  if (!open) return <Button size="sm" variant="ghost" className="text-[#EF4444]" onClick={() => setOpen(true)} data-testid="sms-2ck-revoke-open">Revoca conferma</Button>;
  return (
    <div className="space-y-2 pt-2 border-t border-[#1F2937]" data-testid="sms-2ck-revoke-form">
      <Label className="text-[11px]">Motivazione obbligatoria della revoca (min 10 caratteri)</Label>
      <Textarea className="text-xs min-h-[50px]" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="sms-2ck-revoke-reason" />
      <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={checked} onChange={(e) => setChecked(e.target.checked)} data-testid="sms-2ck-revoke-checkbox" /> Confermo la revoca: il mittente tornerà immediatamente non utilizzabile.</label>
      <div className="flex gap-2">
        <Button size="sm" variant="destructive" disabled={!checked || reason.trim().length < 10} onClick={submit} data-testid="sms-2ck-revoke-submit">Revoca</Button>
        <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>Annulla</Button>
      </div>
    </div>
  );
}

function AuditList() {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/sms/2ck/senders/audit?limit=20").then((r) => setRows(r.data.events || [])).catch(() => setRows([])); }, []);
  if (!rows || rows.length === 0) return null;
  return (
    <details className="text-[11px]" data-testid="sms-2ck-sender-audit">
      <summary className="cursor-pointer text-slate-400 flex items-center gap-1"><History className="h-3 w-3" /> Storico conferme/revoche ({rows.length})</summary>
      <ul className="mt-1 space-y-1">
        {rows.map((r) => (
          <li key={r.id} className="text-muted-foreground" data-testid={`sms-2ck-audit-${r.id}`}>
            {fmt(r.ts)} · <b>{EVENT_IT[r.event] || r.event}</b> · mittente {r.sender_value || "—"} (id {r.sender_id}) · {r.user_email || "sistema"}{r.user_role ? ` (${r.user_role})` : ""}
            {r.source_label && <> · fonte: {r.source_label}, verifica del {r.verified_on}</>}{r.note && <> · nota: “{r.note}”</>}{r.reason && <> · motivo: “{r.reason}”</>}
          </li>
        ))}
      </ul>
    </details>
  );
}

export function Sms2ckSenderConfirm({ st, isSuperAdmin, onChanged }) {
  if (!st?.sender_id) return null;
  const c = st.sender_confirmation || {};
  if (st.sender_confirmed) {
    return (
      <div className="text-xs rounded-lg border border-[#10B981]/40 bg-[#10B981]/10 p-3 space-y-1" data-testid="sms-2ck-sender-confirmed">
        <p className="flex items-center gap-1 text-[#10B981] font-semibold"><ShieldCheck className="h-4 w-4" /> Stato confermato manualmente <span className="font-normal text-muted-foreground">(non verificato automaticamente dal provider)</span></p>
        <p>Mittente <b>{c.confirmed_value || st.sender_value} (id {c.confirmed_id})</b> · da <b>{c.by}</b>{c.role ? ` (${c.role})` : ""} il {fmt(c.at)}</p>
        <p>Fonte: <b>{c.source_label}</b> · verifica effettuata il <b>{c.verified_on}</b></p>
        {c.note && <p className="text-muted-foreground">Nota: “{c.note}”</p>}
        {isSuperAdmin && <RevokeForm senderId={c.confirmed_id} onDone={onChanged} />}
        {isSuperAdmin && <AuditList />}
      </div>
    );
  }
  return (
    <div className="text-xs rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 space-y-1" data-testid="sms-2ck-sender-unconfirmed">
      <p className="flex items-center gap-1 text-amber-600 font-semibold"><ShieldAlert className="h-4 w-4" /> Mittente NON confermato: non utilizzabile per l'invio (SENDER_NOT_CONFIRMED)</p>
      <p className="text-amber-700/90">Selezionato: <b>{st.sender_value || "—"} (id {st.sender_id})</b>. L'API 2CK non espone lo stato del mittente: serve una conferma manuale documentata da parte del Super Admin.</p>
      {c.revoked_at && <p className="text-muted-foreground" data-testid="sms-2ck-sender-revoked-info">Ultima conferma revocata/invalidata il {fmt(c.revoked_at)} da {c.revoked_by}: “{c.revoke_reason}”</p>}
      {isSuperAdmin ? <ConfirmForm senderId={st.sender_id} senderValue={st.sender_value} onDone={onChanged} /> : <p className="text-muted-foreground">Solo il Super Admin può confermare il mittente.</p>}
      {isSuperAdmin && <AuditList />}
    </div>
  );
}
